/**
 * Presents a paid intent checkout as the session-shaped purchase that tracking and confirmation consume, with the
 * reference as `session_id`, so `transaction_id`, `event_id` and the dedupe key equal the reference.
 */
import { getBillingLabel } from '@/components/shared/stripe-checkout-form/helpers'

import {
  type Address,
  type GetCheckoutSessionDetailsResponse,
  type LineItem,
  type Price,
  GetCheckoutSessionDetailsResponseStatus,
  PriceProductType,
} from '@/clients/payment-service'

import { getIntentPrice, type IntentPrice, withGiftsLast } from './intent/prices'
import type {
  IntentCheckoutLine,
  IntentCheckoutResponse,
  IntentCheckoutShippingAddress,
  IntentCheckoutStatus,
} from './intent/types'

export const isIntentPaid = (status: IntentCheckoutStatus): boolean =>
  status === 'CONFIRM_SUCCESS' || status === 'COMPLETED'

/**
 * The line as the price it bills, so session-shaped consumers read the same fields as from a listed price: the
 * product details come from the build-time price, the billing period from the line. A line whose price is missing
 * is named by its description and typed `UNKNOWN_FALLBACK`.
 */
export const toLinePrice = (line: IntentCheckoutLine, currency: string, prices: IntentPrice[]): Price => {
  const listed = getIntentPrice(prices, line.price_lookup_key)
  const productType = listed?.product_type ?? PriceProductType.UNKNOWN_FALLBACK
  return {
    lookup_key: line.price_lookup_key,
    nickname: listed?.nickname ?? line.description,
    currency,
    active: true,
    unit_amount: listed?.unit_amount ?? Math.round(line.amount_subtotal / (line.quantity || 1)),
    product_type: productType,
    tax_behavior: '',
    recurring: line.billing_interval
      ? { interval: line.billing_interval.toUpperCase(), interval_count: line.billing_interval_count ?? 1 }
      : null,
    product_name: listed?.product_name ?? line.description,
    product_image: listed?.product_image ?? null,
    metadata: listed?.metadata ?? {},
    is_fee: productType === PriceProductType.SHIPPING_FEE,
    intro_offer_type: listed?.intro_offer_type ?? null,
  }
}

export const intentLineToLineItem = (
  line: IntentCheckoutLine,
  currency: string,
  index: number,
  prices: IntentPrice[],
): LineItem => {
  const recurring = line.billing_interval
    ? { interval: line.billing_interval, intervalCount: line.billing_interval_count ?? 1 }
    : null
  return {
    id: `${line.price_lookup_key}:${index}`,
    price: toLinePrice(line, currency, prices),
    amount_discount: line.amount_discount,
    amount_subtotal: line.amount_subtotal,
    amount_total: line.amount_total,
    amount_tax: line.amount_tax,
    billing_description: getBillingLabel(recurring, false),
    billing_interval: line.billing_interval,
    billing_interval_count: line.billing_interval_count,
    quantity: line.quantity || 1,
  }
}

const toAddress = (address: IntentCheckoutShippingAddress): Address => ({
  line1: address.line1,
  line2: address.line2 ?? null,
  city: address.city ?? '',
  postal_code: address.postal_code ?? '',
  state: address.state ?? null,
  country_code: address.country ?? '',
})

/**
 * A paid checkout maps to a `COMPLETE` session. A signed-in member's reference holds no email, so `fallbackEmail`
 * stands in.
 */
export const intentCheckoutToPurchase = (
  intent: IntentCheckoutResponse,
  fallbackEmail: string | undefined,
  prices: IntentPrice[],
): GetCheckoutSessionDetailsResponse | null => {
  const { quote } = intent
  // A quote waiting for the address is not a purchase; a paid checkout always holds an address.
  if (!quote || quote.tax_status !== 'CALCULATED') return null
  return {
    session_id: intent.id,
    customer: {
      id: intent.customer_id ?? '',
      email: intent.email ?? fallbackEmail ?? '',
      first_name: intent.customer_first_name ?? null,
      last_name: intent.customer_last_name ?? null,
      full_name: intent.shipping_address?.name ?? null,
      shipping_address: intent.shipping_address ? toAddress(intent.shipping_address) : null,
    },
    status: GetCheckoutSessionDetailsResponseStatus[isIntentPaid(intent.status) ? 'COMPLETE' : 'OPEN'],
    payment_method_type: intent.payment_method_type ?? null,
    line_items: withGiftsLast(quote.lines, prices).map((line, index) =>
      intentLineToLineItem(line, quote.currency, index, prices),
    ),
    amount_total: quote.amount_total,
    amount_subtotal: quote.amount_subtotal,
    amount_tax: quote.amount_tax,
    amount_discount: quote.amount_discount,
    promotion_code: quote.applied_promotion_code,
  }
}
